import { compactNumber } from "@/lib/formatNumber";
import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/components/FileUpload";
import { NEUTRAL_POSTER } from "@/lib/neutralPoster";
import { isHlsSrc, attachHls, prefersNativeHls } from "@/lib/videoSource";
import { peekReelHandoff, clearReelHandoff } from "@/lib/reelHandoff";
import { warmReelTouch } from "@/lib/reelsWarmup";
import { denominationLabel } from "@/lib/denominations";
import { Button } from "@/components/ui/button";
import { Users, CalendarDays, ShoppingBag, MapPin, Check, ChevronRight, Church, BadgeCheck, Film, Eye, Play, Clock } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { countdownInfo } from "@/lib/dateLocale";
import { toast } from "sonner";
import { imageVariant } from "@/lib/mediaUrl";

const MONTHS = ["JAN", "FÉV", "MAR", "AVR", "MAI", "JUIN", "JUIL", "AOÛT", "SEP", "OCT", "NOV", "DÉC"];
// Shared meta line (location, members, followers…) → keeps every suggestion card consistent.
const MetaRow = ({ icon: Icon, children, testId }) => (
  <div className="text-[11px] flex items-center gap-1 mt-0.5" style={{ color: "var(--yalola-text-muted)" }} data-testid={testId}>
    {Icon && <Icon size={10} className="shrink-0" />}<span className="truncate">{children}</span>
  </div>
);
const grad = (id = "") => {
  const g = ["linear-gradient(135deg,#D97757,#E0A63C)", "linear-gradient(135deg,#2E7CF6,#63b3ff)", "linear-gradient(135deg,#2D9E68,#7ed3a6)", "linear-gradient(135deg,#8B5CF6,#c4b5fd)"];
  let h = 0; for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % g.length;
  return g[h];
};

const CONF = {
  groups: { title: "Groupes suggérés", tkey: "groups.suggested", to: "/groups", icon: Users, color: "#2E7CF6" },
  events: { title: "Événements à venir", tkey: "events.upcoming_feed", to: "/events", icon: CalendarDays, color: "#8B5CF6" },
  marketplace: { title: "Dans la Marketplace", tkey: "marketplace.in_feed", to: "/marketplace", icon: ShoppingBag, color: "#2D9E68" },
  churches: { title: "Églises à découvrir", tkey: "churches.suggested_feed", to: "/churches", icon: Church, color: "#2D9E68" },
  reels: { title: "Reels à découvrir", tkey: "reels.suggested_feed", to: "/reels", icon: Film, color: "#D97757" },
};

// Desktop = hover-capable + fine pointer (mouse). Touch devices (mobile/tablet,
// incl. the native app) report no hover → we autoplay the first reel instead.
const CAN_HOVER = typeof window !== "undefined" && window.matchMedia
  ? window.matchMedia("(hover: hover) and (pointer: fine)").matches
  : false;

// A single reel tile in the feed's "Reels à découvrir" strip. Shows the poster,
// and previews the video silently: on desktop while the mouse hovers (up to 5s),
// on touch devices the FIRST tile autoplays ~2.5s once when it scrolls into view.
const ReelDiscoverCard = ({ it, index, onOpen }) => {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const timerRef = useRef(null);
  const hideMsRef = useRef(0);
  const [showVideo, setShowVideo] = useState(false);
  const [posterBroken, setPosterBroken] = useState(false);
  const poster = it.poster_url && !posterBroken ? resolveMediaUrl(it.poster_url) : null;
  // Same source as the Reels player (HLS first) → its manifest/segments are already in the
  // browser cache when the member taps the tile, so the Reels page starts instantly.
  const src = (it.hls_url || it.video_url) ? resolveMediaUrl(it.hls_url || it.video_url) : null;
  const hls = isHlsSrc(src);

  const play = (ms) => {
    if (!src) return;
    hideMsRef.current = ms || 0;   // remembered; the hide timer starts once the video ACTUALLY plays
    clearTimeout(timerRef.current);
    setShowVideo(true);            // mounts an autoPlay muted <video>
  };
  const stop = () => {
    clearTimeout(timerRef.current);
    setShowVideo(false);
  };
  // Start counting the teaser only from the moment playback truly begins, so a
  // slow mobile network can't make it vanish before the first frame shows.
  const onPlaying = () => {
    clearTimeout(timerRef.current);
    if (hideMsRef.current) timerRef.current = setTimeout(() => setShowVideo(false), hideMsRef.current);
  };
  // Some Android WebViews ignore the autoPlay attribute → kick playback explicitly.
  // HLS sources go through hls.js (same path as the Reels player). Back from the Reels
  // page, the teaser resumes at the second the member left (reelHandoff).
  useEffect(() => {
    if (!showVideo) return undefined;
    const v = videoRef.current;
    if (!v) return undefined;
    const resumeAt = peekReelHandoff(it.id);
    const onReady = () => { if (resumeAt > 0) clearReelHandoff(it.id); };
    const nativePath = !hls || prefersNativeHls(v);
    if (resumeAt > 0 && nativePath) {
      const seek = () => { try { v.currentTime = resumeAt; } catch { /* noop */ } };
      if (v.readyState >= 1) seek(); else v.addEventListener("loadedmetadata", seek, { once: true });
    }
    if (hls) return attachHls(v, src, { autoPlay: true, onReady, hlsConfig: resumeAt > 0 ? { startPosition: resumeAt } : undefined });
    const p = v.play(); if (p?.then) p.then(onReady).catch(() => {});
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showVideo]);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Touch devices: first reel autoplays a ~3s teaser once it becomes visible.
  useEffect(() => {
    if (CAN_HOVER || index !== 0 || !src) return;
    const el = wrapRef.current;
    if (!el || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { play(3000); io.disconnect(); } });
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <button
      ref={wrapRef}
      onClick={onOpen}
      onPointerDown={() => warmReelTouch(it)}
      onMouseEnter={CAN_HOVER ? () => play(5000) : undefined}
      onMouseLeave={CAN_HOVER ? stop : undefined}
      className="shrink-0 w-28 rounded-xl overflow-hidden relative snap-start bg-black"
      style={{ height: 192 }}
      data-testid={`feed-sugg-reel-${it.id}`}
    >
      {poster
        ? <img src={poster} loading="lazy" decoding="async" alt="" onError={() => setPosterBroken(true)} className="absolute inset-0 h-full w-full object-cover" data-testid={`feed-sugg-reel-poster-${it.id}`} />
        : <div className="absolute inset-0" style={{ background: grad(it.id) }} data-testid={`feed-sugg-reel-fallback-${it.id}`} />}
      {showVideo && src && (
        <video
          ref={videoRef}
          src={hls ? undefined : src}
          poster={poster || NEUTRAL_POSTER}
          autoPlay
          muted
          playsInline
          preload="auto"
          onPlaying={onPlaying}
          className="absolute inset-0 h-full w-full object-cover"
          data-testid={`feed-sugg-reel-video-${it.id}`}
        />
      )}
      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.72), transparent 55%)" }} />
      <span className="absolute top-2 right-2 h-6 w-6 grid place-items-center rounded-full bg-black/45 text-white"><Play size={12} fill="#fff" /></span>
      <div className="absolute bottom-2 left-2 right-2 text-white">
        <div className="flex items-center gap-1.5">
          {it.author?.avatar_url
            ? <img src={imageVariant(it.author.avatar_url, 48)} loading="lazy" decoding="async" alt="" className="h-5 w-5 rounded-full object-cover border border-white/70" />
            : <span className="h-5 w-5 rounded-full grid place-items-center text-[9px] font-bold border border-white/70" style={{ background: "var(--yalola-primary)" }}>{it.author?.name?.[0]}</span>}
          <span className="text-[11px] font-semibold line-clamp-1 drop-shadow">{it.author?.name}</span>
        </div>
        <div className="text-[10px] flex items-center gap-1 mt-0.5 opacity-90"><Eye size={10} /> {it.views_count || 0}</div>
      </div>
    </button>
  );
};

// Facebook-style inline discovery module injected into the home feed.
export const FeedSuggestions = ({ type }) => {
  const nav = useNavigate();
  const { t, user } = useApp();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState({});
  const [cityBoost, setCityBoost] = useState(false);
  const conf = CONF[type];

  // Rank suggestions by relevance to the member (same denomination / same city).
  const personalize = (list) => {
    const city = (user?.city || "").toLowerCase();
    const denom = (user?.denomination || "").toLowerCase();
    const score = (it) => {
      let s = 0;
      if (denom && (it.denomination || "").toLowerCase() === denom) s += 2;
      if (city && `${it.city || ""} ${it.location || ""}`.toLowerCase().includes(city)) s += 2;
      return s;
    };
    return [...list].sort((a, b) => score(b) - score(a));
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        if (type === "groups") {
          const { data } = await api.get("/groups");
          if (!cancel) setItems(personalize((data.groups || []).filter((g) => !g.is_member)).slice(0, 8));
        } else if (type === "events") {
          const { data } = await api.get("/events");
          if (!cancel) setItems(personalize((data.upcoming || []).filter((e) => !e.is_attending)).slice(0, 8));
        } else if (type === "churches") {
          const { data } = await api.get("/churches/suggestions");
          if (!cancel) {
            const list = (data.churches || []).slice(0, 8);
            setItems(list);
            setCityBoost(list.some((c) => c.same_city));
          }
        } else if (type === "reels") {
          const { data } = await api.get("/reels");
          if (!cancel) setItems((data.reels || []).slice(0, 12));
        } else {
          const { data } = await api.get("/marketplace/listings");
          if (!cancel) setItems(personalize(data.listings || []).slice(0, 10));
        }
      } catch { if (!cancel) setItems([]); }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  if (items === null || items.length === 0) return null;

  const joinGroup = async (g) => {
    setBusy((b) => ({ ...b, [g.id]: true }));
    try { await api.post(`/groups/${g.id}/join`); toast.success(`${t("groups.welcome", "Bienvenue dans")} ${g.name} !`); setItems((l) => l.filter((x) => x.id !== g.id)); }
    catch { toast.error(t("common.error", "Erreur")); setBusy((b) => ({ ...b, [g.id]: false })); }
  };
  const attendEvent = async (e) => {
    setBusy((b) => ({ ...b, [e.id]: true }));
    try { await api.post(`/events/${e.id}/attend`); toast.success(t("suggest.attend_toast", "Tu participes 🎉")); setItems((l) => l.filter((x) => x.id !== e.id)); }
    catch { toast.error(t("common.error", "Erreur")); setBusy((b) => ({ ...b, [e.id]: false })); }
  };
  const followChurch = async (c) => {
    setBusy((b) => ({ ...b, [c.id]: true }));
    try { await api.post(`/churches/${c.id}/follow`); toast.success(t("suggest.follow_toast", "Tu suis désormais {name} 🙏").replace("{name}", c.name)); setItems((l) => l.filter((x) => x.id !== c.id)); }
    catch { toast.error(t("common.error", "Erreur")); setBusy((b) => ({ ...b, [c.id]: false })); }
  };

  return (
    <div className="yalola-card p-4" data-testid={`feed-suggestions-${type}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-full grid place-items-center" style={{ background: `${conf.color}1f` }}><conf.icon size={16} color={conf.color} /></span>
          <h3 className="font-bold font-display text-[15px]">{type === "churches" && cityBoost ? t("churches.near_you", "Églises près de chez toi") : t(conf.tkey, conf.title)}</h3>
        </div>
        <Link to={conf.to} className="text-xs font-semibold hover:underline flex items-center gap-0.5" style={{ color: "var(--yalola-primary)" }} data-testid={`feed-suggestions-seeall-${type}`}>
          {t("common.see_all", "Voir tout")} <ChevronRight size={13} />
        </Link>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1 snap-x">
        {items.map((it, idx) => {
          if (type === "groups") return (
            <div key={it.id} className="shrink-0 w-44 rounded-xl border overflow-hidden snap-start" style={{ borderColor: "var(--yalola-border)" }} data-testid={`feed-sugg-group-${it.id}`}>
              <button className="block w-full" onClick={() => nav(`/groups/${it.id}`)}>
                {it.cover_url ? <img src={resolveMediaUrl(it.cover_url)} loading="lazy" decoding="async" alt="" className="h-20 w-full object-cover" /> : <div className="h-20 w-full" style={{ background: grad(it.id) }} />}
              </button>
              <div className="p-2.5">
                <div className="text-sm font-semibold line-clamp-1">{it.name}</div>
                {it.city && <MetaRow icon={MapPin} testId={`feed-sugg-loc-${it.id}`}>{it.city}</MetaRow>}
                <MetaRow icon={Users}>{t("search.members_count", "{n} membre(s)").replace("{n}", compactNumber(it.member_count))}</MetaRow>
                <Button size="sm" className="w-full rounded-full pill-primary h-8 text-xs mt-2" disabled={busy[it.id]} onClick={() => joinGroup(it)} data-testid={`feed-sugg-join-${it.id}`}>{t("groups.join", "Rejoindre")}</Button>
              </div>
            </div>
          );
          if (type === "events") {
            const d = new Date(it.starts_at);
            const cd = countdownInfo(d, t);
            return (
              <div key={it.id} className="shrink-0 w-44 rounded-xl border overflow-hidden snap-start" style={{ borderColor: "var(--yalola-border)" }} data-testid={`feed-sugg-event-${it.id}`}>
                <button className="block w-full relative" onClick={() => nav("/events")}>
                  {it.cover_url ? <img src={resolveMediaUrl(it.cover_url)} loading="lazy" decoding="async" alt="" className="h-20 w-full object-cover" /> : <div className="h-20 w-full" style={{ background: grad(it.id) }} />}
                  <div className="absolute top-2 left-2 w-10 rounded-md overflow-hidden text-center shadow">
                    <div className="text-[8px] font-bold text-white py-px" style={{ background: "var(--yalola-primary)" }}>{MONTHS[d.getMonth()]}</div>
                    <div className="text-sm font-extrabold bg-white leading-tight">{d.getDate()}</div>
                  </div>
                  {cd && (
                    <span className={`absolute bottom-1.5 right-1.5 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold shadow ${cd.urgent ? "animate-pulse" : ""}`} style={{ background: "var(--yalola-primary)", color: "#fff" }} data-testid={`feed-sugg-countdown-${it.id}`}>
                      <Clock size={9} /> {cd.label}
                    </span>
                  )}
                </button>
                <div className="p-2.5">
                  <div className="text-sm font-semibold line-clamp-2 min-h-[2.5em]">{it.title}</div>
                  {it.location && <MetaRow icon={MapPin} testId={`feed-sugg-loc-${it.id}`}>{it.location}</MetaRow>}
                  <Button size="sm" variant={it.is_attending ? "outline" : "default"} className={`w-full rounded-full h-8 text-xs mt-2 ${it.is_attending ? "" : "pill-primary"}`} disabled={busy[it.id]} onClick={() => attendEvent(it)} data-testid={`feed-sugg-attend-${it.id}`}>
                    {it.is_attending ? <><Check size={12} className="mr-1" /> {t("suggest.attending", "J'y serai")}</> : t("suggest.attend", "Je participe")}
                  </Button>
                </div>
              </div>
            );
          }
          if (type === "churches") return (
            <div key={it.id} className="shrink-0 w-44 rounded-xl border overflow-hidden snap-start" style={{ borderColor: "var(--yalola-border)" }} data-testid={`feed-sugg-church-${it.id}`}>
              <button className="block w-full relative" onClick={() => nav(`/churches/${it.id}`)}>
                {it.cover_url ? <img src={resolveMediaUrl(it.cover_url)} loading="lazy" decoding="async" alt="" className="h-20 w-full object-cover" /> : <div className="h-20 w-full" style={{ background: grad(it.id) }} />}
                {it.logo_url
                  ? <img src={imageVariant(it.logo_url, 48)} loading="lazy" decoding="async" alt="" className="absolute left-2 -bottom-4 h-10 w-10 rounded-lg object-cover border-2 border-white shadow bg-white" />
                  : <div className="absolute left-2 -bottom-4 h-10 w-10 rounded-lg grid place-items-center border-2 border-white shadow" style={{ background: "var(--yalola-primary)" }}><Church size={16} color="#fff" /></div>}
              </button>
              <div className="p-2.5 pt-5">
                <div className="text-sm font-semibold line-clamp-1 flex items-center gap-1">
                  <span className="line-clamp-1">{it.name}</span>
                  {it.managed_by_preacher && <BadgeCheck size={13} className="shrink-0" style={{ color: "var(--yalola-primary)" }} />}
                </div>
                {it.city && <MetaRow icon={MapPin} testId={`feed-sugg-loc-${it.id}`}>{it.city}</MetaRow>}
                <MetaRow icon={it.followers_count ? Users : Church}>{it.followers_count ? t("suggest.followers", "{n} abonné(s)").replace("{n}", compactNumber(it.followers_count)) : (it.denomination ? denominationLabel(t, it.denomination) : t("suggest.church", "Église"))}</MetaRow>
                <Button size="sm" className="w-full rounded-full pill-primary h-8 text-xs mt-2" disabled={busy[it.id]} onClick={() => followChurch(it)} data-testid={`feed-sugg-follow-church-${it.id}`}>
                  {t("church.follow", "Suivre")}
                </Button>
              </div>
            </div>
          );
          if (type === "reels") return (
            <ReelDiscoverCard key={it.id} it={it} index={idx} onOpen={() => nav(`/reels?start=${it.id}`, { state: { back: true, reel: it } })} />
          );
          return (
            <button key={it.id} className="shrink-0 w-36 rounded-xl border overflow-hidden text-left snap-start" style={{ borderColor: "var(--yalola-border)" }} onClick={() => nav(`/marketplace?item=${it.id}`)} data-testid={`feed-sugg-listing-${it.id}`}>
              {it.images?.[0] ? <img src={resolveMediaUrl(it.images[0])} loading="lazy" decoding="async" alt="" className="h-28 w-full object-cover" /> : <div className="h-28 w-full grid place-items-center" style={{ background: "var(--yalola-surface-alt)" }}><ShoppingBag size={24} className="opacity-30" /></div>}
              <div className="p-2.5">
                <div className="text-sm font-bold" style={{ color: "var(--yalola-primary)" }}>{it.price} {it.currency}</div>
                <div className="text-xs font-medium line-clamp-1">{it.title}</div>
                {it.city && <MetaRow icon={MapPin} testId={`feed-sugg-loc-${it.id}`}>{it.city}</MetaRow>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FeedSuggestions;
